/* eslint-disable consistent-return */
import { Spin } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CardiovascularRisk from './CardiovascularRisk';
import KidneyRisk from './KidneyRisk';

import { useAppSelector } from 'app/store';
import Icon from 'common/components/Icon';
import SummaryCard from 'common/components/SummaryCard';
import {
  getCounterHeartRiskHighAndVeryHighV3Service,
  getCounterKidneyRiskStageThreeAndFourV3Service,
  getCounterKidneyRiskTwentyPercentV3Service,
  getCounterResultV3Service,
  getKioskHeartRiskV3Service,
  getKioskKidneyRiskV3Service
} from 'common/services/dashboard';
import { defaultDateRangeMonth } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import HeaderKiosk from 'extends/HeaderKiosk';
import KioskDashboardFilter from 'extends/KioskDashboardFilter';

const RiskOverviewManagement = () => {
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const { t } = useTranslation();
  const [typeParam, setTypeParam] = useState<number | undefined>(undefined);
  const [kiosGroupId, setKiosGroupId] = React.useState<number[]>([]);
  const [dateRange, setDateRange] = React.useState<string[]>(defaultDateRangeMonth);

  const {
    data: kiosHeartData, refetch, isFetching
  } = useQuery(
    ['getKioskHeartRiskV3Query2'],
    () => getKioskHeartRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: kiosKidneyData, refetch: refetchKidney, isFetching: isFetchingKidney
  } = useQuery(
    ['getKioskKidneyRiskV3Query2'],
    () => getKioskKidneyRiskV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: kiosCounterData, refetch: refetchCounter
  } = useQuery(
    ['getCounterResultV3Query2-overview'],
    () => getCounterResultV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: counterHeartRiskData, refetch: refetchHeartRiskCounter
  } = useQuery(
    ['getCounterHeartRiskHighAndVeryHighV3Query2'],
    () => getCounterHeartRiskHighAndVeryHighV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: counterKidneyRiskTwentyPercentData, refetch: refetchKidneyRiskTwentyPercent
  } = useQuery(
    ['getCounterKidneyRiskTwentyPercentV3Query2-overview'],
    () => getCounterKidneyRiskTwentyPercentV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const {
    data: counterKidneyRiskStageThreeAndFourData, refetch: refetchKidneyRiskStageThreeAndFour
  } = useQuery(
    ['getCounterKidneyRiskStageThreeAndFourV3Query2'],
    () => getCounterKidneyRiskStageThreeAndFourV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
  );

  const handleClear = () => {
    setDateRange([]);
    setTypeParam(undefined);
    setKiosGroupId([]);
  };

  return (
    <div className="riskOverview">
      <HeaderKiosk
        icon={<Icon iconName="pie2" />}
        title={t('dashboard2025.riskOverview')}
      />
      <div className="riskOverview-wrapper">
        <KioskDashboardFilter
          typeParam={typeParam}
          setTypeParam={setTypeParam}
          kiosGroupId={kiosGroupId}
          setKiosGroupId={setKiosGroupId}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onClear={handleClear}
          onRefetch={refetch}
          onRefetch2={refetchKidney}
          onRefetch3={refetchCounter}
          onRefetch4={refetchHeartRiskCounter}
          onRefetch5={refetchKidneyRiskTwentyPercent}
          onRefetch6={refetchKidneyRiskStageThreeAndFour}
        />
        <div className="riskOverview-card">
          <SummaryCard
            variant="tim-mach"
            title={t('dashboard2025.total')}
            number={kiosCounterData?.total || 0}
          />
          <SummaryCard
            variant="tim-mach"
            title={t('dashboard2025.riskTotal')}
            number={counterHeartRiskData?.total || 0}
          />
          <SummaryCard
            title={t('dashboard2025.kidneyTotal')}
            number={counterKidneyRiskTwentyPercentData?.total || 0}
          />
          <SummaryCard
            title={t('dashboard2025.kidneyStage345')}
            number={counterKidneyRiskStageThreeAndFourData?.total || 0}
          />
        </div>
        <div className="riskOverview-charts">
          {getPermission(rolesUser, roles.REPORT_HEART_RIS_V3) && (
            <Spin spinning={isFetching}>
              <CardiovascularRisk
                cardiovascularRisk={{
                  veryHigh: kiosHeartData?.find((item) => item.heartRiskCode === 'veryHigh')?.total || 0,
                  high: kiosHeartData?.find((item) => item.heartRiskCode === 'high')?.total || 0,
                  lowAndMedium: kiosHeartData?.find((item) => item.heartRiskCode === 'lowAndMedium')?.total || 0,
                  missingHeartRisk: kiosHeartData?.find((item) => item.heartRiskCode === 'missingHeartRisk')?.total || 0,
                }}
                style={{ minHeight: 600 }}
              />
            </Spin>
          )}
          {getPermission(rolesUser, roles.REPORT_KIDNEY_RISK_V3) && (
            <Spin spinning={isFetchingKidney}>
              <KidneyRisk
                kidneyRisk={{
                  zeroPercent: kiosKidneyData?.find((item) => item.cdk === 'zeroPercent')?.total || 0,
                  missing: kiosKidneyData?.find((item) => item.cdk === 'missing')?.total || 0,
                  twentyPercent: kiosKidneyData?.find((item) => item.cdk === 'twentyPercent')?.total || 0,
                  stage_3: kiosKidneyData?.find((item) => item.cdk === 'stage_3')?.total || 0,
                  stage_4_5: kiosKidneyData?.find((item) => item.cdk === 'stage_4_5')?.total || 0,
                }}
                style={{ minHeight: 600 }}
              />
            </Spin>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskOverviewManagement;
